import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import LockedScoreCard from './shared/LockedScoreCard';
import AsSeenOnStrip from '@components/quiz/shared/AsSeenOnStrip';
import CtaButton from '@components/quiz/shared/CtaButton';
import TrustpilotRating from '@components/quiz/shared/TrustpilotRating';
import { useReadiness } from '@components/quiz/ReadinessProvider';
import type { SanityReadinessEmailGateBlock } from '@lib/sanity/types';

type EmailGateBlockProps = SanityReadinessEmailGateBlock & {
  onSubmit: (email: string) => void;
  isSubmitting: boolean;
};

const emailSchema = z.object({
  email: z.string().email()
});

type EmailFormValues = z.infer<typeof emailSchema>;

/**
 * Step 7: the hard email gate. The locked score card shows the bar
 * filled to the user's true position with the winners marker visible,
 * but the score pill stays obscured ("??") until after the reveal.
 */
const EmailGateBlock: FC<EmailGateBlockProps> = ({
  obscuredScoreLabel,
  scoreCardLabel,
  scoreCardLockedLabel,
  loginLinkText,
  loginUrl,
  emailPlaceholder,
  consentText,
  invalidEmailText,
  ctaText,
  trustpilotTemplateId,
  trustpilotHeight,
  trustpilotWidth,
  asSeenOnLabel,
  asSeenOnItems,
  asSeenOnMonochrome,
  asSeenOnLogoHeight,
  onSubmit,
  isSubmitting
}) => {
  const { liveReadiness, liveRace } = useReadiness();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    mode: 'onChange'
  });

  const submit = handleSubmit((data) => {
    if (isSubmitting) return;
    onSubmit(data.email);
  });

  return (
    <section className="flex min-h-[50vh] flex-col items-center justify-between px-6 pb-10 pt-8">
      <div className="w-full max-w-sm">
        {/* Locked score card — true position, obscured figure. Post-GW1
            prefers the race positions once a real league is selected. */}
        <LockedScoreCard
          fillPercent={liveRace?.userPercent ?? liveReadiness?.score ?? 0}
          targetPercent={
            liveRace?.pacePercent ?? liveReadiness?.winnersTarget ?? 80
          }
          label={scoreCardLabel ?? 'YOUR READINESS SCORE'}
          lockedLabel={scoreCardLockedLabel ?? 'Ready to reveal'}
          obscuredScoreLabel={obscuredScoreLabel ?? '??'}
        />

        <div className="mt-6 rounded-2xl border border-neutral-200 bg-white px-5 py-4">
          <TrustpilotRating
            templateId={trustpilotTemplateId}
            height={trustpilotHeight}
            width={trustpilotWidth}
          />
          {asSeenOnItems && asSeenOnItems.length > 0 && (
            <>
              <div className="my-4 border-t border-dashed border-neutral-200" />
              <AsSeenOnStrip
                items={asSeenOnItems}
                label={asSeenOnLabel}
                monochrome={asSeenOnMonochrome}
                logoHeight={asSeenOnLogoHeight}
              />
            </>
          )}
        </div>

        <form onSubmit={submit} className="mt-6" noValidate>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder={emailPlaceholder ?? 'Your email address'}
            data-cy="readiness-email-input"
            className={`w-full rounded-xl border bg-white px-[18px] py-3.5 font-body text-sm text-neutral-800 outline-none transition-all focus:border-primary-600 ${
              errors.email ? 'border-danger-400' : 'border-neutral-300'
            }`}
            {...register('email')}
          />
          {errors.email && (
            <p className="mt-2 font-body text-sm text-danger-600">
              {invalidEmailText ?? 'Please enter a valid email address.'}
            </p>
          )}
          {consentText && (
            <p className="mt-3 text-center font-body text-xs text-neutral-500">
              {consentText}
            </p>
          )}
        </form>
      </div>

      <div className="mt-10 w-full max-w-sm">
        <CtaButton
          onClick={submit}
          disabled={!isValid || isSubmitting}
          dataCy="readiness-email-cta"
        >
          {isSubmitting
            ? 'One moment…'
            : ctaText ?? 'Reveal my readiness score'}
        </CtaButton>
        {loginLinkText && (
          <a
            href={loginUrl ?? '/auth/login'}
            data-cy="readiness-email-login-link"
            className="mt-3 block w-full text-center font-body text-sm font-bold text-primary-500 transition-colors hover:text-primary-600"
          >
            {loginLinkText}
          </a>
        )}
      </div>
    </section>
  );
};

export default EmailGateBlock;
